import { useMemo, useState } from "react";
import { X, Search, Pill, Check } from "lucide-react";

const eur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0, useGrouping: "always" }).format(n || 0);

/** Pantalla de selección de grupos terapéuticos para pedir solo una campaña
 *  (antibióticos, antihistamínicos…) en lugar del pedido completo. */
export const TherapeuticPicker = ({ groups = [], selected = [], labs = [], expandLab = "", onApply, onClose }) => {
  const [picked, setPicked] = useState(new Set(selected));
  const [q, setQ] = useState("");
  const [lab, setLab] = useState(expandLab);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return groups;
    return groups.filter(
      (g) =>
        g.family.toLowerCase().includes(needle) ||
        (g.indications || []).some((i) => i.toLowerCase().includes(needle)),
    );
  }, [groups, q]);

  const toggle = (family) => {
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(family) ? next.delete(family) : next.add(family);
      return next;
    });
  };

  const totals = useMemo(() => {
    const sel = groups.filter((g) => picked.has(g.family));
    return {
      units: sel.reduce((a, g) => a + g.units, 0),
      eur: sel.reduce((a, g) => a + g.eur, 0),
      lines: sel.reduce((a, g) => a + g.lines, 0),
    };
  }, [groups, picked]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="therapeutic-picker"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 rounded-xl bg-indigo-600 grid place-items-center shrink-0">
              <Pill className="h-5 w-5 text-white" strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="font-display text-xl font-extrabold text-slate-900 leading-tight">
                Pedir por grupo terapéutico
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Elige los grupos de la campaña y el pedido se queda solo con esas líneas.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors" data-testid="therapeutic-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar grupo o indicación (antibiótico, alergia…)"
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="therapeutic-search"
            />
          </div>
          <button
            onClick={() => setPicked(new Set(visible.map((g) => g.family)))}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900"
            data-testid="therapeutic-select-all"
          >
            Seleccionar todos
          </button>
          <button
            onClick={() => setPicked(new Set())}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900"
            data-testid="therapeutic-clear"
          >
            Ninguno
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {visible.map((g) => {
              const on = picked.has(g.family);
              return (
                <button
                  key={g.family}
                  onClick={() => toggle(g.family)}
                  data-testid={`therapeutic-group-${g.family}`}
                  className={`text-left px-4 py-3 rounded-xl border transition-colors ${
                    on ? "border-indigo-600 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-slate-900">{g.family}</span>
                    <span
                      className={`h-5 w-5 rounded-md grid place-items-center shrink-0 ${
                        on ? "bg-indigo-600" : "border border-slate-300"
                      }`}
                    >
                      {on && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 truncate">
                    {(g.indications || []).join(" · ")}
                  </div>
                  <div className="text-xs text-slate-600 font-mono-num tabular-nums mt-1.5">
                    {g.lines} líneas · {g.units} uds · {eur(g.eur)}
                  </div>
                </button>
              );
            })}
          </div>
          {visible.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">No hay grupos que coincidan con la búsqueda.</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 bg-indigo-50/40">
          <label className="block text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-900/70 mb-2">
            Aprovechar una oferta: llevar toda la gama del grupo a un proveedor
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={lab}
              onChange={(e) => setLab(e.target.value)}
              className="text-sm px-3 py-2 border border-indigo-200 rounded-lg bg-white max-w-sm"
              data-testid="therapeutic-expand-lab"
            >
              <option value="">No, solo lo que ya le compro</option>
              {labs.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
            <span className="text-xs text-slate-600 max-w-md">
              Trae a ese proveedor todas las moléculas del grupo que fabrica y que tu farmacia dispensa, aunque hoy se
              las compres a otro. Las líneas se mueven, no se duplican.
            </span>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
          <div className="text-sm text-slate-600" data-testid="therapeutic-selection-summary">
            {picked.size === 0 ? (
              "Sin grupos elegidos: se pedirá todo"
            ) : (
              <>
                <strong className="text-slate-900">{picked.size}</strong> grupos ·{" "}
                <strong className="text-slate-900 font-mono-num">{totals.lines}</strong> líneas ·{" "}
                <strong className="text-slate-900 font-mono-num">{totals.units}</strong> uds ·{" "}
                <strong className="text-slate-900 font-mono-num">{eur(totals.eur)}</strong>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-600 hover:text-slate-900"
            >
              Cancelar
            </button>
            <button
              onClick={() => onApply([...picked], lab)}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-lg text-sm font-bold transition-colors"
              data-testid="therapeutic-apply"
            >
              {picked.size === 0 ? "Ver pedido completo" : "Aplicar al pedido"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
